import React, { useContext, useEffect, useMemo, useState } from "react";
import FoodCard from "../components/FoodCard";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { StoreContext } from "../context/StoreContext";
import { TailSpin } from "react-loader-spinner";
import OtpAuthDialog from "../components/OtpAuthDialog";

const Cart = () => {
  const {
    setOfferData,
    deviceId,
    userId,
    accessToken,
    cartData,
    setCartData,
    prevRestaurantId,
    roadId,
    setRoadId,
  } = useContext(StoreContext);
  const navigate = useNavigate();
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [restaurantId, setRestaurantId] = useState(prevRestaurantId);
  const [menuItems, setMenuItems] = useState([]);
  const [showCart, setShowCart] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);
  const [menuLoading, setMenuLoading] = useState(false);

  const isLoading = useMemo(() => {
    // When we don't have a restaurant, we skip menu fetch.
    return cartLoading || (Boolean(restaurantId) && menuLoading);
  }, [cartLoading, menuLoading, restaurantId]);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        if (!deviceId) return;
        setCartLoading(true);
        const response = await axios.get(`/api/nexus/cart/fetch/v3/${deviceId}`);

        if (response.data?.data) {
          // Align with StoreContext cart structure to keep quantities in sync
          const serverCart = {};
          response.data.data.items?.forEach((item) => {
            if (item.type === "BASIC") {
              serverCart[item.dishId] = {
                count: item.count,
                addOnItems: item.addOnItems || [],
              };
            } else if (item.type === "VARIANT") {
              serverCart[item.dishId] = {};
              item.variants?.forEach((variant) => {
                serverCart[item.dishId][variant.variantId] = {
                  count: variant.count,
                  addOnItems: variant.addOnItems || [],
                };
              });
            }
          });

          setCartData(serverCart);
          setRestaurantId(response.data.data.restaurantId);
          if (response.data.data.positionGeohash) {
            setRoadId(response.data.data.positionGeohash);
          }
        } else {
          setCartData({});
          setRestaurantId(undefined);
        }
      } catch (error) {
        console.error("Error fetching cart items:", error);
      } finally {
        setCartLoading(false);
      }
    };

    fetchCart();
  }, [deviceId, setCartData, setRoadId]);

  // Keep restaurantId in sync if it becomes available later from context
  useEffect(() => {
    if (!restaurantId && prevRestaurantId) {
      setRestaurantId(prevRestaurantId);
    }
  }, [prevRestaurantId, restaurantId]);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        setMenuLoading(true);
        const requestData = {
          restaurantId: restaurantId,
          filters: [{ type: "AVAILABILITY", value: true }],
        };

        const response = await axios.post("/api/catalogue/restaurant/menu", requestData, {
          headers: { "Content-Type": "application/json" },
        });

        setMenuItems(response?.data?.dishes || []);
      } catch (err) {
        console.error("Error fetching menu items:", err);
      } finally {
        setMenuLoading(false);
      }
    };

    if (restaurantId) fetchMenuItems();
  }, [restaurantId]);

  useEffect(() => {
    if (!menuItems.length || !cartData || Object.keys(cartData).length === 0) {
      setShowCart([]);
      return;
    }

    const filteredCart = menuItems
      .filter((menuItem) => cartData[menuItem.dishId])
      .map((menuItem) => {
        const cartEntry = cartData[menuItem.dishId];
        let quantity = 0;

        if (typeof cartEntry === "number") {
          // Legacy support
          quantity = cartEntry;
        } else if (cartEntry?.count !== undefined) {
          // Basic dish structure { count, addOnItems }
          quantity = cartEntry.count;
        } else if (typeof cartEntry === "object" && cartEntry !== null) {
          // Variant dish - sum variant counts
          quantity = Object.values(cartEntry).reduce(
            (sum, variant) => sum + (variant?.count || 0),
            0
          );
        }

        return {
          ...menuItem,
          quantity,
        };
      });

    setShowCart(filteredCart);
  }, [cartData, menuItems]);

  return (
    <div className="pt-24 flex flex-col w-full h-full min-h-screen">
      {/* Header */}
      <h1 className="w-full flex justify-center text-2xl font-semibold">Your Cart</h1>

      {/* Loading spinner */}
      {isLoading ? (
        <div className="flex justify-center items-center flex-1">
          <TailSpin
                      height="80"
                      width="80"
                      color="#FBC02D"
                      ariaLabel="loading"
                      radius="1"
                      visible={true}
                    />
        </div>
      ) : (
        <>
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto mt-5">
            {showCart.length > 0 ? (
              showCart.map((item, index) => (
                <FoodCard
                  key={index}
                  name={item.name}
                  price={item.price}
                  rating={item.rating}
                  ratingCounts={item.ratingCounts}
                  thumbnailUrl={item.thumbnailUrl}
                  dishId={item.dishId}
                  restaurantId={item.restaurantId}
                  roadId={roadId}
                  quantity={item.quantity}
                  availableNow={item.availableNow ?? true}
                  variations={item.variations}
                  addOnGroups={item.addOnGroups}
                  description={
                    item.description || item.shortDescription || item.itemDescription || ""
                  }
                  foodPreference={item.foodPreference}
                  defaultPriciestVariant={Boolean(item.defaultPriciestVariant)}
                />
              ))
            ) : (
              <p className="text-center">Your cart is empty</p>
            )}
          </div>

          {/* Checkout Button */}
          <div className="flex justify-center mb-20">
            <button
              onClick={() => {
                setOfferData(null);
                if (!userId || !accessToken) {
                  setShowOtpDialog(true);
                } else {
                  navigate("/checkout");
                }
              }}
              className="bg-[#FBC02D] text-white px-8 py-4 w-[90%] md:w-[50%] rounded-lg"
            >
              Proceed to Checkout
            </button>
          </div>
        </>
      )}

      <OtpAuthDialog
        isOpen={showOtpDialog}
        onClose={() => setShowOtpDialog(false)}
        onSuccess={() => {
          setShowOtpDialog(false);
          navigate("/checkout");
        }}
      />
    </div>
  );
};

export default Cart;
